// agentx — edge-native AI agent runtime over a virtual filesystem.

export { Agent, AgentOptions } from './Agent';
export type { RunResult, CompactResult } from './Agent';
export { estimateTokens } from './Agent';

export { MODEL_ALIASES, resolveModelAlias, modelShortLabel, resolveModelSwitch } from './models';
export type { ResolveModelSwitchOpts, ModelSwitch } from './models';

export { NodeDiskFilesystem } from './NodeDiskFilesystem';
export { BodDbFilesystem } from './BodDbFilesystem';
export { JailedFilesystem, JailOptions, DEFAULT_DENY } from './JailedFilesystem';
export { OverlayFilesystem, checkpointTool, rollbackTool, checkpointTools } from './OverlayFilesystem';
export { MountFilesystem } from './MountFilesystem';
export type { Mount } from './MountFilesystem';
export { raceAttempts } from './speculate';
export type { Attempt } from './speculate';
export { validateToolCode, compileSynthesizedTool } from './synthesize';
export type { ToolSpec } from './synthesize';
export { PermissionPolicy, PermissionOptions, planMode, composeHooks, DEFAULT_MUTATING } from './permissions';
export type { Decision, PermissionRule } from './permissions';

export { FakeAIClient, toolCall } from './FakeAIClient';

export { bashTool, readTool, editTool, defaultTools, exitSessionTool, makeContext, toWireTools, toolRegistry, toolsByName } from './tools';
export { JobRegistry, JobRegistryOptions, SandboxJobRegistry, makeJobTools, formatJobNotice } from './tools.jobs';
export type { JobStatus as RegistryJobStatus, JobKind, JobInfo, JobNotice, JobHandle, JobTrackOptions, JobCtx } from './tools.jobs';
export { Scheduler, makeScheduleTools, parseCron, cronMatches, nextCronAfter } from './scheduler';
export type { ScheduledJob, ScheduledJobSnapshot, Trigger, TriggerOneOff, TriggerInterval, TriggerCron, SchedulerOptions } from './scheduler';
export { sandboxAgentOptions, diskAgentOptions, fullAgentOptions } from './presets';
export { grepTool, globTool, makeGrepTool, makeGlobTool, DEFAULT_SKIP_DIRS, DEFAULT_MAX_DEPTH, type FileScanOptions, writeTool, multiEditTool, applyEditsTool, repoMapTool, repoIndex, mkdirp } from './tools.structured';
export { todoWriteTool } from './todo';
export { webFetchTool, webSearchTool, webSearchAnthropicTool, makeWebFetchTool, makeWebSearchTool, makeWebSearchAnthropicTool, htmlToText, parseDdgHtml, decodeDdgUrl } from './tools.web';
export { Scratch, makeAskTool, SCRATCH_DIR } from './scratch';
export type { ScratchOptions, AskOptions } from './scratch';
export type { WebFetchOptions, WebSearchOptions } from './tools.web';
export type { TodoItem } from './todo';
export type { AgentTool, ToolContext, HostBridge, HostEvent, UserQuestion } from './tools';

export { askUserQuestionTool, ScriptedHostBridge, ConsoleHostBridge } from './host';

export { loadSkills, scanSkills } from './skills';
export type { SkillInfo } from './skills';
export { tokenize, relevanceScore, topByRelevance, idfWeights } from './relevance';
export { loadCommands, scanCommands, expandCommand, expandTemplate, expandEntry } from './commands';
export type { CommandInfo, SiblingResolver } from './commands';
export { loadMemory, writeFact, slugify, MEMORY_PROMPT, VOICE_MEMORY_PROMPT } from './memory';
export type { LoadMemoryOpts } from './memory';
export { lessonCapture, LessonOptionsDefaults } from './lessons';
export type { LessonOptions } from './lessons';
export { reflectOnRun } from './reflect';
export type { ReflectOptions } from './reflect';
export { loadInstructions } from './instructions';

export { DuplexAgent, DuplexAgentOptions, VOICE_SYSTEM_PROMPT } from './duplex';
export type { TaskRecord, DuplexTaskStatus, WorkerTier } from './duplex';

export { makeTaskTool, makeTaskBatchTool } from './subagent';
export type { TaskToolOptions } from './subagent';
export { loadAgents } from './agents';
export type { AgentDef } from './agents';
export { mcpToolsToAgentTools, mcpToolToAgentTool, makeMcpToolSearch, makeMcpToolSearchFromMounted, makeLazyMcpToolSearch, buildMcpCatalog } from './mcp';
export type { McpToolSpec, McpCall, McpToolSearchOptions, McpImage, McpToolResult, MountedMcpLike, McpRoute, McpRouteResolver } from './mcp';
export { RecordingHooks, RecordingLifecycle } from './hooks';
export type { Hooks, ToolUse, ToolUseMeta, PreToolUseDecision } from './hooks';

export { forComponent, log } from './logging';

// Voice I/O (portable core — CLI/server/browser; platform backends live in cli/voice.ts).
export { VoiceEngine, VoiceEngineOptions } from './voice/engine';
export type { SttLike, TtsLike } from './voice/engine';
export { SonioxSTT, SonioxSTTOptions } from './voice/soniox';
export { CartesiaTTS, CartesiaTTSOptions } from './voice/cartesia';
export { resolveAuth, STT_SAMPLE_RATE, TTS_SAMPLE_RATE } from './voice/types';
export type { AudioSource, AudioSink, AuthProvider, VoiceState } from './voice/types';

export type { Message, Tool, ToolCall, ChatResponse, ChatOptions, ChatLike, Role, StreamChunk, ContentPart, MessageContent } from './llm';
export { contentText, imagePart, contentBytes, elideStaleImages, elideInlineImages, elideImages, messageHasInlineImage, IMAGE_ELIDE_STUB, imageRefPart, parseImageRef, messageHasImageRef, messageHasImage, imageRefResult, toolImageRef, expandImagesForSend, IMAGE_REF_SCHEME, mintImageRef, clearImageRefs, sendBytes, sendBytesPer, remintImageRefs } from './llm';
export type { ImageRef } from './llm';
export { reasoningToChatFragment } from './reasoning';
export type { ReasoningEffort, ChatFragment } from './reasoning';

export { validateWorktreeSlug, worktreeBranchName, getOrCreateWorktree, cleanupWorktree, enterWorktree, exitWorktree, getWorktreeSession, findGitRoot } from './worktree';
export type { WorktreeInfo, WorktreeSession } from './worktree';

// Re-export the wcli-core filesystem backends so embedders get everything from one place.
export { MemFilesystem, IndexedDbFilesystem, CommandExecutor, registerHeadlessCommands } from '@livx.cc/wcli/core';
export type { IFilesystem, FileMetadata } from '@livx.cc/wcli/core';
